// latest price map from backend oracle, with local symbol aliases/fallbacks

import { useQuery } from '@tanstack/react-query';
import { getChainName } from '@ui/constants/chain';
import { getIsFreezeLivePrice } from '@ui/constants/isDebug';
import { Interval } from '@ui/constants/poll';
import { tryReportError } from '@ui/lib/bugsnag';
import { queryClient } from '@ui/lib/reactQuery';
import { useBackendPairsAll } from '@ui/services/backend/hooks/getPairs/useBackendPairsAll';
import { oracleLatestPriceBatchQuery } from '@ui/services/oracle/latest-price/getOraclePriceBatch';
import {
  ORACLE_SUPPORTED_SYMBOLS,
} from '@ui/services/oracle/latest-price/types';
import {
  STATIC_SUPPORTED_SYMBOLS,
} from '@ui/services/pyth-hermes/constants';
import { Token } from '@ui/types/token';
import { Nullable } from '@ui/types/utility';
import BigNumber from 'bignumber.js';

import { useDataChainId } from '../datachain';
import {
  SAME_PRICE_MAP,
  SupportedSymbol,
  WRAPPED_NATIVE_MAP,
} from './priceLatest/store';

export const usePriceLatest = (
  symbol?: SupportedSymbol,
  refetchIntervalMs?: number,
) => {
  const { data: map } = usePriceMapLatest(refetchIntervalMs);
  if (!symbol) return;

  return map?.[symbol];
};

export const useTokenPriceLatest = (token: Nullable<Token>) => {
  const { data: map } = usePriceMapLatest();
  if (!token) return;
  if (STATIC_SUPPORTED_SYMBOLS.includes(token.symbol as any)) {
    return map?.[token.symbol as SupportedSymbol];
  }
  // TODO: use token address to fetch price from backend
  return undefined;
};

export const usePriceMapLatest = (refetchIntervalMs?: number) => {
  const chainId = useDataChainId();
  const isFreeze = getIsFreezeLivePrice();
  const { data: pairs } = useBackendPairsAll();
  const oraclePairs = Object.values(pairs ?? {}).map((pair) => ({
    pairBase: pair.address,
    baseSymbol: pair.baseSymbol,
  }));
  const oraclePriceBatchKey = oracleLatestPriceBatchQuery.getKey({
    pairs: oraclePairs,
    chainId,
  });

  return useQuery({
    refetchOnWindowFocus: !isFreeze,
    refetchOnMount: !isFreeze,
    refetchInterval: isFreeze ? false : refetchIntervalMs,
    queryKey: [
      'usePriceLatest',
      chainId,
      Object.keys(pairs ?? {}),
      oraclePriceBatchKey,
      ORACLE_SUPPORTED_SYMBOLS,
      STATIC_SUPPORTED_SYMBOLS,
      isFreeze,
    ],
    staleTime: Interval.VERYFAST,
    // cacheTime: Interval.FAST,
    queryFn: async () => {
      // const timings: Record<string, number> = {};
      // const startTotal = performance.now();
      const results = await Promise.allSettled([
        queryClient.ensureQueryData({
          queryKey: oraclePriceBatchKey,
          queryFn: oracleLatestPriceBatchQuery.getFn({
            pairs: oraclePairs,
            chainId,
          }),
        }),
      ] as const);

      const [oraclePrices] = results;
      const priceMap: Partial<Record<SupportedSymbol, BigNumber>> = {};

      if (oraclePrices.status === 'fulfilled') {
        const targetSymbols = new Set<SupportedSymbol>([
          ...Object.values(pairs ?? {}).map(
            (v) => v.baseSymbol as SupportedSymbol,
          ),
          ...STATIC_SUPPORTED_SYMBOLS,
          ...ORACLE_SUPPORTED_SYMBOLS,
        ]);
        oraclePrices.value.forEach(({ symbol, price }) => {
          if (targetSymbols.has(symbol as SupportedSymbol)) {
            priceMap[symbol as SupportedSymbol] = price;
          }
        });
      } else {
        tryReportError(
          new Error('Failed to fetch oraclePrices', {
            cause: oraclePrices.reason,
          }),
        );
      }

      if (!priceMap.USDC) {
        priceMap.USDC = new BigNumber(1);
      }
      if (!priceMap.LVUSD) {
        priceMap.LVUSD = priceMap.USDC;
      }
      if (!priceMap.MON && priceMap.WMON) {
        priceMap.MON = priceMap.WMON;
      }
      if (!priceMap.LVMON && priceMap.MON) {
        priceMap.LVMON = priceMap.MON;
      }

      // Apply price aliases at the end
      Object.entries(SAME_PRICE_MAP).forEach(([baseSymbol, aliasSymbols]) => {
        if (priceMap[baseSymbol as SupportedSymbol]) {
          aliasSymbols.forEach((aliasSymbol) => {
            priceMap[aliasSymbol] = priceMap[baseSymbol as SupportedSymbol]!;
          });
        }
      });

      // WRAPPED_NATIVE map
      priceMap['WRAPPED_NATIVE'] =
        priceMap[WRAPPED_NATIVE_MAP[getChainName(chainId)]];

      if (isFreeze) {
      }

      return priceMap;
    },
  });
};
